/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {addClassNamesToElement} from '@lexical/utils';
import {ElementNode} from 'lexical';

function $convertLayoutContainerElement(domNode) {
  const styleAttributes = window.getComputedStyle(domNode);
  const templateColumns = styleAttributes.getPropertyValue(
    'PlaygroundEditorTheme__layoutContainer',
  );
  if (templateColumns) {
    const node = $createLayoutContainerNode(templateColumns);
    return {node};
  }
  return null;
}

export class LayoutContainerNode extends ElementNode {
  __templateColumns;

  constructor(templateColumns, key) {
    super(key);
    this.__templateColumns = templateColumns;
  }

  static getType() {
    return 'layout-container';
  }

  static clone(node) {
    return new LayoutContainerNode(node.__templateColumns, node.__key);
  }

  createDOM(config) {
    const dom = document.createElement('div');
    dom.style.gridTemplateColumns = this.__templateColumns;
    if (typeof config.theme.layoutContainer === 'string') {
              dom.className = 'PlaygroundEditorTheme__layoutContainer'; // Add this line

      addClassNamesToElement(dom, config.theme.layoutContainer);
    }
    return dom;
  }

  exportDOM() {
    const element = document.createElement('div');
        element.className = 'PlaygroundEditorTheme__layoutContainer'; // Add this line

    element.style.gridTemplateColumns = this.__templateColumns;
    element.setAttribute('data-lexical-layout-container', 'true');
    return {element};
  }

  updateDOM(prevNode, dom) {
    if (prevNode.__templateColumns !== this.__templateColumns) {
      dom.style.gridTemplateColumns = this.__templateColumns;
    }
    return false;
  }

static importDOM() {
    return {
      div: (node) => {
        // Check if this div has the layout container attribute
        if (node.hasAttribute('data-lexical-layout-container')) {
          return {
            conversion: (domNode) => {
              const layoutContainer = $createLayoutContainerNode();
              
              // Restore the grid template columns from style
              if (domNode.style.gridTemplateColumns) {
                layoutContainer.setTemplateColumns(domNode.style.gridTemplateColumns);
              }
              
              return {
                node: layoutContainer,
              };
            },
            priority: 2,
          };
        }
        return null;
      },
    };
  }

  static importJSON(json) {
    return $createLayoutContainerNode().updateFromJSON(json);
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      templateColumns: this.getTemplateColumns(),
      type: 'layout-container',
      version: 1,
    };
  }

  getTemplateColumns() {
    return this.__templateColumns;
  }

  setTemplateColumns(templateColumns) {
    const writable = this.getWritable();
    writable.__templateColumns = templateColumns;
  }
}

export function $createLayoutContainerNode(templateColumns = '1fr 1fr') {
  return new LayoutContainerNode(templateColumns);
}

export function $isLayoutContainerNode(node) {
  return node instanceof LayoutContainerNode;
}
